import React, { useEffect, useRef, useState } from "react";
import { Card, ModernCardHeader } from "../../../components/cards";
import { Button } from "../../../components/button";
import { QrCodeScanner } from "../../stock-units/components";
import { useScanStockUnit } from "../../stock-scans/hooks";
import { ScanAction } from "../../../__generated__/globalTypes";

const SUCCESS_DISPLAY_MS = 1000;

type IScanResult =
  | { kind: "success"; label: string }
  | { kind: "error"; message: string };

export const QuickPickupScanWidget: React.FC = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<IScanResult | null>(null);
  const { scan } = useScanStockUnit();

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleScan = async (code: string) => {
    if (busy || result) return;

    setBusy(true);
    const stockUnit = await scan({ input: { qrCode: code, action: ScanAction.PickUp } });
    setBusy(false);

    if (stockUnit) {
      setResult({
        kind: "success",
        label: `${stockUnit.product?.name} — Lot ${stockUnit.batchNumber}`,
      });
      successTimerRef.current = setTimeout(() => {
        setResult(null);
      }, SUCCESS_DISPLAY_MS);
    } else {
      setResult({ kind: "error", message: "Code non reconnu ou déjà sorti" });
    }
  };

  const dismissError = () => setResult(null);

  return (
    <Card>
      <ModernCardHeader
        title="Prise de stock rapide"
        subtitle="Scannez un carton pour l'enregistrer comme pris en charge"
      />

      {!isScanning ? (
        <Button className="w-full mt-3" onClick={() => setIsScanning(true)}>
          Démarrer le scan
        </Button>
      ) : (
        <div className="relative mt-3">
          <QrCodeScanner active={!result} onScan={handleScan} />

          <div className="mt-3">
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                setIsScanning(false);
                setResult(null);
              }}
            >
              Arrêter le scan
            </Button>
          </div>

          {result && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded">
              {result.kind === "success" ? (
                <div className="bg-white rounded-lg px-6 py-5 text-center shadow-xl">
                  <p className="text-2xl mb-1">✅</p>
                  <p className="font-semibold text-gray-900">Carton pris en charge</p>
                  <p className="text-xs text-gray-500 mt-1">{result.label}</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg px-6 py-5 text-center shadow-xl max-w-xs">
                  <p className="text-2xl mb-1">⚠️</p>
                  <p className="font-semibold text-red-600">Échec de la lecture</p>
                  <p className="text-sm text-gray-600 mt-1">{result.message}</p>
                  <Button className="mt-4" onClick={dismissError}>
                    Continuer le scan
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
